import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DateFilter, getRanges } from '../../common/filters';
import { AircraftFilter } from './AircraftFilter';
import { EventFilter } from './EventFilter';
import { SeverityFilter } from './SeverityFilter';
import { Context as SisenseContext } from '../../../context/InsightsSisenseContextSafetyEvents';
import { insightsDashboardUnavailableError } from '../../../helpers';
import { useSelector, useDispatch } from 'react-redux';
import {
  getSelectedAircraft,
  setSelectedAircraft,
  getSelectedEventTypes,
  setSelectedEventTypes,
  setSelectedSeverityTypes,
  getDates,
  setDates,
  resetDates,
  getSelectedSeverityType
} from '../../../redux/slice/safetyInsights';
import moment from 'moment';

/**
 * Todo: Add Sisense types when they become available
 */
const today = new Date().toISOString();

export const SisenseFilters = ({ datasource, aircraftdim, eventTypeDim, severityTypeDim }) => {
  const dates = useSelector(getDates);
  const selectedAircraft = useSelector(getSelectedAircraft);
  const selectedEventTypes = useSelector(getSelectedEventTypes);
  const selectedSeverityTypes = useSelector(getSelectedSeverityType);
  const dispatch = useDispatch();

  const {
    sisense,
    aircraftFilters,
    eventFilterValues,
    severityFilterValues,
    sisenseDashboardReady,
    sisenseDashboardBeingExported
  } = useContext(SisenseContext);
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState('');

  // event filter related
  const [eventSearchText, setEventSearchText] = useState('');
  const [eventFiltervisibility, setEventFiltervisibility] = useState(false);

  // severity filter related
  const [severityTypeSearchText, setSeverityTypeSearchText] = useState('');
  const [severityFilterVisibility, setSeverityFilterVisibility] = useState(false);

  const applyAircraftFilter = aircraft => {
    const isEmptyFilter = Array.isArray(aircraft) && aircraft.length === 0;
    try {
      if (isEmptyFilter) {
        sisense.dashboard.applyFilters({
          jaql: {
            datasource,
            dim: aircraftdim,
            filter: {}
          }
        });
      } else {
        sisense.dashboard.applyFilters({
          jaql: {
            datasource,
            dim: aircraftdim,
            datatype: 'type',
            filter: { members: [...aircraft], multiSelection: true, explicit: true }
          }
        });
      }
    } catch (e) {
      console.log(e);
      insightsDashboardUnavailableError();
    }
  };

  const applyEventTypeFilter = eventType => {
    const isEmptyFilter = Array.isArray(eventType) && eventType.length === 0;
    try {
      if (isEmptyFilter) {
        sisense.dashboard.applyFilters({
          jaql: {
            datasource,
            dim: eventTypeDim,
            filter: {}
          }
        });
      } else {
        sisense.dashboard.applyFilters([
          {
            jaql: {
              datasource,
              dim: eventTypeDim,
              datatype: 'type',
              filter: {
                multiSelection: true,
                explicit: true,
                members: [...eventType]
              }
            }
          }
        ]);
      }
    } catch (e) {
      console.log(e);
      insightsDashboardUnavailableError();
    }
  };

  const applySeverityTypeFilter = severityType => {
    const isEmptyFilter = Array.isArray(severityType) && severityType.length === 0;
    try {
      if (isEmptyFilter) {
        sisense.dashboard.applyFilters({
          jaql: {
            datasource,
            dim: severityTypeDim,
            filter: {}
          }
        });
      } else {
        sisense.dashboard.applyFilters({
          jaql: {
            datasource,
            dim: severityTypeDim,
            datatype: 'type',
            filter: {
              multiSelection: true,
              members: [...severityType],
              explicit: true
            }
          }
        });
      }
    } catch (e) {
      console.log(e);
      insightsDashboardUnavailableError();
    }
  };

  const applyDateFilters = (from, to) => {
    try {
      sisense.dashboard.applyFilters({
        jaql: {
          dim: '[DimDates.Date (Calendar)]',
          datatype: 'datetime',
          level: 'days',
          filter: {
            from,
            to
          }
        }
      });
      applyAircraftFilter(selectedAircraft); // Fixes bug CEX-452, issues is with sisense
      applyEventTypeFilter(selectedEventTypes);
    } catch (e) {
      console.log(e);
      insightsDashboardUnavailableError();
    }
  };

  useEffect(() => {
    // Requried to run once to update pdf export date filter to one shown in web UI
    if (sisense?.dashboard && sisenseDashboardReady && dates.dashboardFrom && dates.dashboardTo) {
      applyDateFilters(dates.dashboardFrom, dates.dashboardTo);
    }
    if (sisense?.dashboard && sisenseDashboardReady) {
      /*
      Due to an unknown reason, a severity filter is applied by default
      when the dashboard is loaded. This filter and all other filters that may exist are removed here.
      dashborad.clearFilters() or dashboard.removeFilters() cause unintended side effects, so resorting to using this long method
      to remove all filters.
      */

      sisense.dashboard.applyFilters([
        {
          jaql: {
            dim: severityTypeDim,
            filter: {}
          }
        },
        {
          jaql: {
            dim: eventTypeDim,
            filter: {}
          }
        },
        {
          jaql: {
            dim: aircraftdim,
            filter: {}
          }
        }
      ]);
    }
  }, [sisenseDashboardReady]);

  const updateDateFilter = dates => {
    if (!dates.length) {
      dispatch(resetDates());
      applyDateFilters('2019-01-01', today);
    }
    if (sisense && !!dates.length && dates !== null && dates[0] && dates[1]) {
      const from = dates[0].format('YYYY-MM-DD');
      const to = dates[1].format('YYYY-MM-DD');

      dispatch(
        setDates({
          dashboardFrom: from,
          dashboardTo: to
        })
      );
      applyDateFilters(from, to);
    }
  };

  const clearAircraftFilter = () => {
    dispatch(setSelectedAircraft([]));
    applyAircraftFilter([]);
    setVisible(false);
    setSearchText('');
  };

  const confirmAircraftFilter = () => {
    applyAircraftFilter(selectedAircraft);
    setVisible(false);
    setSearchText('');
  };

  const confirmEventTypeFilter = () => {
    applyEventTypeFilter(selectedEventTypes);
    setEventFiltervisibility(false);
    setEventSearchText('');
  };

  const clearEventTypeFilter = () => {
    dispatch(setSelectedEventTypes([]));
    applyEventTypeFilter([]);
    setEventFiltervisibility(false);
    setEventSearchText('');
  };

  const confirmSeverityTypeFilter = () => {
    applySeverityTypeFilter(selectedSeverityTypes);
    setSeverityFilterVisibility(false);
    setSeverityTypeSearchText('');
  };

  const clearSeverityTypeFilter = () => {
    dispatch(setSelectedSeverityTypes([]));
    applySeverityTypeFilter([]);
    setSeverityFilterVisibility(false);
    setSeverityTypeSearchText('');
  };

  const selectAircraft = aircraft => dispatch(setSelectedAircraft(aircraft));
  const selectEventTypes = eventTypes => dispatch(setSelectedEventTypes(eventTypes));
  const selectSeverityTypes = severityTypes => dispatch(setSelectedSeverityTypes(severityTypes));

  let calendarFieldsValues;

  if (!dates.dashboardFrom && !dates.dashboardTo) {
    calendarFieldsValues = [undefined, undefined];
  } else {
    calendarFieldsValues = [moment(dates.dashboardFrom), moment(dates.dashboardTo)];
  }
  const ranges = getRanges();

  return (
    <React.Fragment>
      <DateFilter
        ranges={ranges}
        handleChange={updateDateFilter}
        calendarFieldsValues={calendarFieldsValues}
        disabled={!sisenseDashboardReady || sisenseDashboardBeingExported}
      />
      <AircraftFilter
        searchText={searchText}
        setSearchText={setSearchText}
        visible={visible}
        setVisible={setVisible}
        filters={aircraftFilters}
        aircraftdim={aircraftdim}
        selectAircraft={selectAircraft}
        selectedAircraft={selectedAircraft}
        clearAircraftFilter={clearAircraftFilter}
        confirmFilter={confirmAircraftFilter}
        disabled={!sisenseDashboardReady || sisenseDashboardBeingExported}
      />
      <EventFilter
        searchText={eventSearchText}
        setSearchText={setEventSearchText}
        visible={eventFiltervisibility}
        setVisible={setEventFiltervisibility}
        filters={eventFilterValues}
        selectEventTypes={selectEventTypes}
        selectedEventTypes={selectedEventTypes}
        clearEventTypeFilter={clearEventTypeFilter}
        confirmFilter={confirmEventTypeFilter}
        disabled={!sisenseDashboardReady || sisenseDashboardBeingExported}
      />
      <SeverityFilter
        searchText={severityTypeSearchText}
        setSearchText={setSeverityTypeSearchText}
        visible={severityFilterVisibility}
        setVisible={setSeverityFilterVisibility}
        filters={severityFilterValues}
        selectSeverityTypes={selectSeverityTypes}
        selectedSeverityTypes={selectedSeverityTypes}
        clearSeverityTypeFilter={clearSeverityTypeFilter}
        confirmFilter={confirmSeverityTypeFilter}
        disabled={!sisenseDashboardReady || sisenseDashboardBeingExported}
      />
    </React.Fragment>
  );
};

SisenseFilters.propTypes = {
  datasource: PropTypes.string,
  datedim: PropTypes.string,
  aircraftdim: PropTypes.string,
  eventTypeDim: PropTypes.string,
  severityDim: PropTypes.string
};

export default SisenseFilters;
